import {
  Component,
  createRef,
  lazy,
  Suspense,
  type ErrorInfo,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from "react";
import type { ErrorBoundaryCardProps } from "./ErrorBoundaryCard.js";

/**
 * Sprint 17 Wave F — top-level error boundary.
 *
 * Renders a calm fallback when a render-time error escapes the
 * component tree (a hook throw, a TypeError on an unexpected
 * shape, a 5xx wrapped by react-query that bubbles past its own
 * error UI). The fallback surfaces the request-id from
 * `ApiError.requestId` (Sprint 16 Wave H) when available so an
 * operator can correlate to the structured access log.
 *
 * Out of scope: error reporting to a service, retry-with-key
 * machinery, route-level boundaries. Sprint 18+ if needed.
 *
 * D-WEB-BOUNDARY-FALLBACK-UX1 — this class also guards the ROOT of the
 * tree (`main.tsx`), OUTSIDE `<LocaleProvider>`; `useLocale()` throws
 * there. So `t` is an OPTIONAL prop: the route boundary in `App.tsx`
 * (which sits inside `<LocaleProvider>`) passes it, the root boundary
 * does not and keeps literal English strings, which double as their
 * own fallback. Two more changes from that package: a second,
 * hard-navigation "go home" escape next to "Try again" (a deterministic
 * render throw reproduces immediately on reset, proven by this file's
 * tests), and focus moving to the alert card so a screen-reader user
 * lands on it without hunting.
 *
 * E-BUNDLE-126 (F2 aus BUNDLE_AUDIT_2026-09-21.md) — die Karte selbst
 * (Titel, Body, `<pre>`, Request-ID, Knoepfe) liegt jetzt in
 * `ErrorBoundaryCard.tsx` und kommt per `lazy()` nach; im Einstiegs-
 * Chunk bleiben nur die Klasse, `getDerivedStateFromError`, der Fokus
 * und das `role="alert"`-Gehaeuse. Der Fehlerpfad der Boundary haengt
 * bewusst an KEINEM nachgeladenen Modul: das Gehaeuse mit dem
 * Ein-Satz-Fallback rendert synchron, waehrend die Karte laedt; und
 * scheitert das Nachladen selbst (Chunk offline, Deploy zwischen zwei
 * Hashes), faengt `.catch()` am `import()` das ab und zeigt denselben
 * Satz plus den harten „Zur Startseite"-Link — statt einen zweiten
 * Fehler in eine Boundary zu werfen, die schon einen haelt.
 */

type Translate = (key: string, vars?: Record<string, string | number>) => string;

/** Die zwei Literale, die der Ein-Satz-Fallback ohne `t` braucht. */
const PLAIN_EN = {
  title: "Something went wrong.",
  goHome: "Go to homepage",
} as const;

/**
 * Unformatierter Ein-Satz-Fallback: steht, waehrend die Karte laedt,
 * und bleibt stehen, wenn sie nicht laden kann (`withLink`). Bewusst
 * ohne Styles und ohne Fehlermeldung — das ist Aufgabe der Karte.
 */
function PlainFallback({
  t,
  withLink,
}: {
  t?: Translate | undefined;
  withLink: boolean;
}): ReactElement {
  return (
    <p>
      {t ? t("common.error_boundary.title") : PLAIN_EN.title}
      {withLink ? (
        <>
          {" "}
          <a href="/">{t ? t("common.error_boundary.go_home") : PLAIN_EN.goHome}</a>
        </>
      ) : null}
    </p>
  );
}

const ErrorBoundaryCard = lazy(async () => {
  try {
    const mod = await import("./ErrorBoundaryCard.js");
    return { default: mod.ErrorBoundaryCard };
  } catch (cause) {
    // Der Karten-Chunk laedt nicht (offline, alter Hash nach einem
    // Deploy). Nicht werfen: ein Throw hier landet in dieser Boundary,
    // die gerade den Fehler haelt — an der Root waere das die weisse
    // Seite. Stattdessen den Satz mit Ausweg dauerhaft anzeigen.
    console.error("ErrorBoundary card chunk failed to load", cause);
    return {
      default: ({ t }: ErrorBoundaryCardProps) => <PlainFallback t={t} withLink />,
    };
  }
});

interface ErrorBoundaryProps {
  readonly children: ReactNode;
  /** From `useLocale()`. Optional — see the class doc comment above. */
  readonly t?: Translate;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  private readonly alertRef: RefObject<HTMLDivElement | null> = createRef();

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Use console.error directly: at this point the React tree is
    // already in an error state and the structured logger may not
    // be reachable. Stack/info goes to the browser console where
    // an operator can capture it.
    console.error("ErrorBoundary caught", { error, info });
    // D-WEB-BOUNDARY-FALLBACK-UX1 — move focus to the alert card the
    // moment it appears, so a screen-reader/keyboard user lands on it
    // instead of on whatever had focus in the tree that just threw.
    // `componentDidCatch` runs after the fallback DOM commits whether
    // this is the first render (throw during mount) or a later one
    // (throw after a reset), unlike `componentDidUpdate`, which never
    // fires for the first case.
    this.alertRef.current?.focus();
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }
    const t = this.props.t;
    return (
      <div
        role="alert"
        tabIndex={-1}
        ref={this.alertRef}
        style={{
          padding: "var(--gt-sys-space-6)",
          maxInlineSize: 640,
          marginInline: "auto",
          marginBlock: "var(--gt-sys-space-6)",
          background: "var(--gt-sys-surface)",
          borderRadius: "var(--gt-sys-radius-md)",
          border: "1px solid var(--gt-sys-border)",
        }}
      >
        <Suspense fallback={<PlainFallback t={t} withLink={false} />}>
          <ErrorBoundaryCard error={this.state.error} t={t} onReset={this.reset} />
        </Suspense>
      </div>
    );
  }
}
